import { useEffect, useState } from 'react';
import Dialogo from '../../components/ui/Dialogo';
import ModalLlaveAcceso from '../../components/ModalLlaveAcceso';
import { btnPrimario, btnSecundario, campo, pesos } from '../../components/ui/estilos';
import { comandasService, modificadoresService, productosService } from '../../services/api';
import type { Cobro } from './useCobro';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
const n = (v: string | undefined) => parseFloat(v || '0') || 0;

const pastilla = (on: boolean): React.CSSProperties => ({
  height: 44, padding: '0 16px', borderRadius: 999, fontSize: 15, fontWeight: 700, cursor: 'pointer',
  background: on ? 'var(--brand)' : 'var(--surface)', color: on ? 'var(--on-brand)' : 'var(--ink)',
  border: on ? 'none' : '1px solid var(--line-strong)',
});
const pie = (onCancelar: () => void, onOk: () => void, ok: string, deshabilitado = false) => (
  <div style={{ display: 'flex', gap: 10 }}>
    <button onClick={onCancelar} style={{ ...btnSecundario, flex: 1, height: 52 }}>Cancelar</button>
    <button onClick={onOk} disabled={deshabilitado} style={{ ...btnPrimario, flex: 2, height: 52, fontSize: 16, opacity: deshabilitado ? .5 : 1 }}>{ok}</button>
  </div>
);

/* ─── Descuento: lo autoriza un admin o cajero con su correo y contraseña ─── */
export function DialogoDescuento({ c, pcts, onCerrar }: { c: Cobro; pcts: number[]; onCerrar: () => void }) {
  const [email, setEmail] = useState('');
  const [pass, setPass] = useState('');
  const [error, setError] = useState('');
  const [verificando, setVerificando] = useState(false);
  const [item, setItem] = useState<string | null>(null);
  const [propio, setPropio] = useState(c.descTipo === 'monto' ? c.descValor : '');

  const autorizar = async () => {
    if (!email || !pass) { setError('Captura correo y contraseña.'); return; }
    setVerificando(true); setError('');
    try {
      const r = await fetch(`${API}/auth/login`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, password: pass }) });
      const d = await r.json();
      if (!r.ok || !d.token) { setError('Correo o contraseña incorrectos.'); return; }
      if (!['admin', 'cajero'].includes(d.usuario?.rol)) { setError('Solo un admin o cajero puede autorizar descuentos.'); return; }
      c.autorizar(d.usuario.nombre);
    } catch { setError('Sin conexión con el servidor.'); }
    finally { setVerificando(false); setPass(''); }
  };

  if (!c.autorizadoPor) return (
    <Dialogo titulo="Autorizar descuento" onCerrar={onCerrar} ancho={440}>
      <p style={{ margin: 0, color: 'var(--muted)' }}>Lo autoriza un gerente (admin o cajero).</p>
      <input type="email" autoComplete="off" placeholder="Correo" value={email} onChange={e => setEmail(e.target.value)} style={campo} aria-label="Correo de quien autoriza" />
      <input type="password" autoComplete="off" placeholder="Contraseña" value={pass} onChange={e => setPass(e.target.value)} onKeyDown={e => e.key === 'Enter' && autorizar()} style={campo} aria-label="Contraseña de quien autoriza" />
      {error && <div role="alert" style={{ color: 'var(--danger)', fontWeight: 700 }}>{error}</div>}
      {pie(onCerrar, autorizar, verificando ? 'Verificando…' : 'Autorizar', verificando)}
    </Dialogo>
  );

  const cta = c.cuenta;
  const pctActual = c.descTipo === 'porcentaje' ? n(c.descValor) : 0;
  return (
    <Dialogo titulo="Aplicar descuento" onCerrar={onCerrar} ancho={520}>
      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--muted)' }}>A toda la cuenta</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        {pcts.map(p => <button key={p} onClick={() => c.descuentoCuenta('porcentaje', String(p))} aria-pressed={pctActual === p} style={pastilla(pctActual === p)}>{p}%</button>)}
        <input type="number" min="0" inputMode="decimal" placeholder="Monto $" value={propio} aria-label="Descuento en pesos a toda la cuenta"
          onChange={e => { setPropio(e.target.value); c.descuentoCuenta('monto', e.target.value); }} style={{ ...campo, width: 130, height: 44 }} />
      </div>

      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--muted)' }}>O a un producto</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 300, overflowY: 'auto' }}>
        {cta.items.map((i: any) => {
          const d = n(c.descItems[i.id]), abierto = item === i.id;
          return (
            <div key={i.id} style={{ border: `1px solid ${abierto ? 'var(--brand-texto)' : 'var(--line)'}`, borderRadius: 12 }}>
              <button onClick={() => setItem(abierto ? null : i.id)} aria-expanded={abierto}
                style={{ width: '100%', display: 'flex', justifyContent: 'space-between', gap: 8, padding: '12px 14px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink)', fontSize: 16, textAlign: 'left' }}>
                <span>{i.cantidad}× {i.producto}{d > 0 && <span style={{ color: 'var(--danger)', fontWeight: 700 }}> · −{pesos(d)}</span>}</span>
                <span style={{ fontWeight: 700 }}>{pesos(i.importe - d)}</span>
              </button>
              {abierto && (
                <div style={{ padding: '0 14px 12px', display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                  {pcts.map(p => <button key={p} onClick={() => c.fijarDescItem(i.id, i.importe, i.importe * p / 100)} style={{ ...pastilla(Math.abs(d - i.importe * p / 100) < .01 && d > 0), height: 40 }}>{p}%</button>)}
                  <button onClick={() => c.fijarDescItem(i.id, i.importe, i.importe)} style={{ ...pastilla(d >= i.importe - .01), height: 40 }}>Cortesía</button>
                  <input type="number" min="0" inputMode="decimal" placeholder="$" aria-label={`Descuento en pesos a ${i.producto}`} defaultValue={d || ''}
                    onChange={e => c.fijarDescItem(i.id, i.importe, n(e.target.value))} style={{ ...campo, width: 100, height: 40 }} />
                  {d > 0 && <button onClick={() => c.fijarDescItem(i.id, i.importe, 0)} style={{ ...btnSecundario, height: 40 }}>Quitar</button>}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {c.montoDescuento > 0 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 14px', borderRadius: 12, background: 'var(--well)', fontSize: 17, fontWeight: 700 }}>
          <span>Descuento (aut. {c.autorizadoPor})</span><span>−{pesos(c.montoDescuento)}</span>
        </div>
      )}
      <div style={{ display: 'flex', gap: 10 }}>
        <button onClick={() => { c.quitarDescuentos(); setPropio(''); }} style={{ ...btnSecundario, flex: 1, height: 52 }}>Quitar descuentos</button>
        <button onClick={onCerrar} style={{ ...btnPrimario, flex: 2, height: 52, fontSize: 16 }}>Listo</button>
      </div>
    </Dialogo>
  );
}

/* ─── Unir: la otra cuenta se pasa completa a la que está abierta ─── */
export function DialogoUnir({ titulo, candidatos, onElegir, onCerrar }: {
  titulo: string; candidatos: { key: string; label: string; total: number; origen: any }[];
  onElegir: (origen: any, label: string) => void; onCerrar: () => void;
}) {
  return (
    <Dialogo titulo="Unir con otra cuenta" onCerrar={onCerrar} ancho={460}>
      <p style={{ margin: 0, color: 'var(--muted)' }}>Selecciona la cuenta que se suma a {titulo}. Se cobra todo en un solo ticket.</p>
      {candidatos.map(k => (
        <button key={k.key} onClick={() => onElegir(k.origen, k.label)} style={{ ...btnSecundario, height: 56, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderRadius: 12 }}>
          <span>{k.label}</span><span>{pesos(k.total)}</span>
        </button>
      ))}
      {!candidatos.length && <p style={{ margin: 0, color: 'var(--muted)' }}>No hay otras cuentas abiertas que se puedan unir.</p>}
      <button onClick={onCerrar} style={{ ...btnSecundario, height: 52 }}>Cancelar</button>
    </Dialogo>
  );
}

/* ─── Cargo extra (descorche, servicio…) ─── */
export function DialogoCargo({ comandaId, onListo, onCerrar }: { comandaId: string; onListo: () => void; onCerrar: () => void }) {
  const [concepto, setConcepto] = useState('');
  const [monto, setMonto] = useState('');
  const [error, setError] = useState('');
  const valido = concepto.trim() && n(monto) > 0;
  const guardar = async () => {
    if (!valido) { setError('Captura concepto y monto.'); return; }
    try { await comandasService.addCargo(comandaId, concepto.trim(), n(monto)); onListo(); }
    catch (e: any) { setError(e.response?.data?.error || 'No se pudo agregar el cargo.'); }
  };
  return (
    <Dialogo titulo="Cargo extra" onCerrar={onCerrar} ancho={440}>
      <input autoFocus placeholder="Concepto (descorche, servicio…)" maxLength={80} value={concepto} onChange={e => setConcepto(e.target.value)} style={campo} aria-label="Concepto" />
      <input type="number" min="0" inputMode="decimal" placeholder="Monto" value={monto} onChange={e => setMonto(e.target.value)} style={campo} aria-label="Monto" />
      {error && <div role="alert" style={{ color: 'var(--danger)', fontWeight: 700 }}>{error}</div>}
      {pie(onCerrar, guardar, 'Agregar cargo', !valido)}
    </Dialogo>
  );
}

/* ─── Abrir cajón a mano (queda en auditoría con quién lo abrió) ─── */
export function DialogoCajon({ onCerrar }: { onCerrar: () => void }) {
  const [pass, setPass] = useState('');
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  const abrir = async () => {
    if (!pass) { setError('Captura tu contraseña.'); return; }
    setError('');
    try {
      const r = await fetch(`${API}/configuracion/abrir-cajon-manual`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ password: pass }),
      });
      const d = await r.json();
      if (!r.ok) { setError(d.error || 'Error del servidor.'); return; }
      if (!d.ok) { setError(d.msg || 'No se pudo abrir el cajón.'); return; }
      setOk(`Cajón abierto · ${d.nombre}`);
      setTimeout(onCerrar, 1800);
    } catch { setError('Sin conexión con el servidor.'); }
    finally { setPass(''); }
  };
  return (
    <Dialogo titulo="Abrir cajón" onCerrar={onCerrar} ancho={420}>
      {ok ? <div role="status" style={{ padding: 16, borderRadius: 12, background: 'var(--ok-bg)', color: 'var(--ok)', fontWeight: 700, textAlign: 'center' }}>{ok}</div> : <>
        <input autoFocus type="password" autoComplete="off" placeholder="Tu contraseña" value={pass} onChange={e => setPass(e.target.value)} onKeyDown={e => e.key === 'Enter' && abrir()} style={campo} aria-label="Contraseña" />
        {error && <div role="alert" style={{ color: 'var(--danger)', fontWeight: 700 }}>{error}</div>}
        {pie(onCerrar, abrir, 'Abrir cajón')}
      </>}
    </Dialogo>
  );
}

/* ─── Editar productos de la cuenta (quitar o agregar sin pasar por el salón) ─── */
export function DialogoProductos({ cuenta, onCambio, onCerrar }: { cuenta: any; onCambio: () => Promise<void>; onCerrar: () => void }) {
  const [productos, setProductos] = useState<any[]>([]);
  const [busca, setBusca] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { productosService.getAll().then(r => setProductos(r.data.filter((p: any) => p.disponible && !p.suspendido))).catch(() => {}); }, []);

  // Complementos (pollo extra…) de un producto ya pedido: se abren bajo su renglón.
  const [itemMods, setItemMods] = useState<{ id: string; opciones: any[] } | null>(null);
  const abrirMods = async (i: any) => {
    if (itemMods?.id === i.id) { setItemMods(null); return; }
    setError('');
    try {
      const r = await modificadoresService.getDeProducto(i.producto_id);
      const opciones = r.data.flatMap((g: any) => (g.opciones || []).filter((o: any) => o.operacion !== 'quitar'));
      if (!opciones.length) { setError(`"${i.producto}" no tiene complementos configurados.`); return; }
      setItemMods({ id: i.id, opciones });
    } catch { setError('No se pudieron cargar los complementos.'); }
  };

  // Nota de un producto ya enviado a cocina (llega al KDS al guardar).
  const [nota, setNota] = useState<{ id: string; texto: string } | null>(null);

  // Una cuenta de mesa junta varias rondas: cada producto se edita en su propia comanda.
  // Pedido ya confirmado: el servidor pide contraseña para quitar; se reintenta con ella.
  const [pideLlave, setPideLlave] = useState<{ cambio: object; comandaId: string } | null>(null);
  const cambiar = async (cambio: object, comandaId = cuenta.comanda_id) => {
    setOcupado(true); setError('');
    try { await comandasService.updateItems(comandaId, cambio); await onCambio(); return true; }
    catch (e: any) {
      if (e.response?.data?.requiere_autorizacion) setPideLlave({ cambio, comandaId });
      else setError(e.response?.data?.error || 'No se pudo cambiar la cuenta.');
      return false;
    }
    finally { setOcupado(false); }
  };
  const q = busca.trim().toLowerCase();

  return (
    <Dialogo titulo="Editar productos" onCerrar={onCerrar} ancho={520}>
      <div style={{ display: 'flex', flexDirection: 'column', maxHeight: 320, overflowY: 'auto' }}>
        {cuenta.items.map((i: any) => (
          <div key={i.id} style={{ padding: '8px 0', borderBottom: '1px solid var(--line-2)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ flex: 1, fontSize: 16 }}>
                <b>{i.cantidad}×</b> {i.producto}
                {i.notas && <span style={{ display: 'block', fontSize: 14, color: 'var(--muted)' }}>{i.notas}</span>}
              </span>
              <button disabled={ocupado} onClick={() => setNota(nota?.id === i.id ? null : { id: i.id, texto: i.notas || '' })}
                aria-expanded={nota?.id === i.id} style={{ ...btnSecundario, height: 40 }}>Nota</button>
              <button disabled={ocupado} onClick={() => abrirMods(i)} aria-expanded={itemMods?.id === i.id}
                style={{ ...btnSecundario, height: 40 }}>+ Complemento</button>
              <button disabled={ocupado} onClick={() => window.confirm(`¿Quitar "${i.producto}" de la cuenta?`) && cambiar({ eliminar: [i.id] }, i.item_comanda_id)}
                style={{ ...btnSecundario, height: 40, color: 'var(--danger)' }}>Quitar</button>
            </div>
            {nota?.id === i.id && (
              <div style={{ display: 'flex', gap: 6, paddingTop: 8 }}>
                <input autoFocus placeholder="Ej. sin cebolla, aderezo aparte…" value={nota?.texto || ''} aria-label={`Nota para ${i.producto}`}
                  onChange={e => setNota({ id: i.id, texto: e.target.value })}
                  onKeyDown={e => e.key === 'Enter' && cambiar({ notas_items: [{ item_id: i.id, notas: nota?.texto || '' }] }, i.item_comanda_id).then(ok => ok && setNota(null))}
                  style={{ ...campo, flex: 1, height: 40 }} />
                <button disabled={ocupado} onClick={() => cambiar({ notas_items: [{ item_id: i.id, notas: nota?.texto || '' }] }, i.item_comanda_id).then(ok => ok && setNota(null))}
                  style={{ ...btnPrimario, height: 40 }}>Guardar</button>
              </div>
            )}
            {itemMods?.id === i.id && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, paddingTop: 8 }}>
                {itemMods?.opciones.map((o: any) => (
                  <button key={o.id} disabled={ocupado}
                    onClick={() => cambiar({ modificar: [{ item_id: i.id, opciones: [o.id] }] }, i.item_comanda_id).then(() => setItemMods(null))}
                    style={{ ...pastilla(false), height: 40 }}>
                    {o.nombre}{parseFloat(o.precio_extra) > 0 && ` +${pesos(parseFloat(o.precio_extra))}`}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      <input placeholder="Buscar producto para agregar…" value={busca} onChange={e => setBusca(e.target.value)} style={campo} aria-label="Buscar producto" />
      {q && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 220, overflowY: 'auto' }}>
          {productos.filter(p => p.nombre.toLowerCase().includes(q)).slice(0, 20).map(p => (
            <button key={p.id} disabled={ocupado} onClick={() => cambiar({ agregar: [{ producto_id: p.id, cantidad: 1 }] })}
              style={{ ...btnSecundario, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>{p.nombre}</span><span>{pesos(parseFloat(p.precio))}</span>
            </button>
          ))}
        </div>
      )}
      {error && <div role="alert" style={{ color: 'var(--danger)', fontWeight: 700 }}>{error}</div>}
      <button onClick={onCerrar} style={{ ...btnPrimario, height: 52, fontSize: 16 }}>Listo</button>
      {pideLlave && (
        <ModalLlaveAcceso permiso="puede_modificar_comanda" titulo="Quitar producto confirmado"
          descripcion="El pedido ya está en preparación. Autoriza con la contraseña de quien puede modificar comandas."
          onCancel={() => setPideLlave(null)}
          onSuccess={(_, password) => { const p = pideLlave; setPideLlave(null); cambiar({ ...p.cambio, password }, p.comandaId); }} />
      )}
    </Dialogo>
  );
}

/* ─── Pago registrado: el cambio a devolver, grande ─── */
export function PagoExitoso({ pago, onReimprimir, onListo }: { pago: any; onReimprimir?: () => void; onListo: () => void }) {
  const cifra = (k: string, v: number, grande = false) => (
    <div style={{ padding: 16, borderRadius: 12, background: 'var(--surface)', border: grande ? '2px solid var(--ok-line)' : '1px solid var(--line)' }}>
      <div style={{ fontSize: 15, color: 'var(--muted)' }}>{k}</div>
      <div style={{ fontSize: grande ? 34 : 26, fontWeight: 700, color: grande ? 'var(--ok)' : 'var(--ink)' }}>{pesos(v)}</div>
    </div>
  );
  return (
    <div role="status" style={{ maxWidth: 560, padding: 24, borderRadius: 16, background: 'var(--ok-bg)', border: '1px solid var(--ok-line)', display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--ok)' }}>Pago registrado</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {cifra('Total cobrado', parseFloat(pago.monto_total))}
        {parseFloat(pago.propina) > 0 && cifra('Propina', parseFloat(pago.propina))}
        {parseFloat(pago.cambio) > 0 && cifra('Cambio a devolver', parseFloat(pago.cambio), true)}
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        {onReimprimir && <button onClick={onReimprimir} style={{ ...btnSecundario, flex: 1, height: 52 }}>Reimprimir ticket</button>}
        <button onClick={onListo} style={{ ...btnPrimario, flex: 1, height: 52, fontSize: 16 }}>Listo</button>
      </div>
    </div>
  );
}
